import './coapplicantIncomeDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const {
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      incomeAmountValue: inputEventHandler.currency.formatter(
        coapplicantAdditionalIncomeAmount,
      ),
      incomeDurationMonthsValue: coapplicantAdditionalIncomeDurationMonths,
      incomeDurationYearsValue: coapplicantAdditionalIncomeDurationYears,
      incomeEmployerNameValue: coapplicantAdditionalIncomeEmployerName,
      incomeFrequencyValue: coapplicantAdditionalIncomeFrequency,
      incomeJobTitleValue: coapplicantAdditionalIncomeJobTitle,
      incomeTypeValue: coapplicantAdditionalIncomeType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      incomeTypeValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeAmountValue,
    } = values;
    if (!incomeTypeValue || !incomeFrequencyValue || !incomeAmountValue) {
      return;
    }

    setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
    setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
    setCoapplicantAdditionalIncomeType(incomeTypeValue);

    if (showSecondaryEmploymentFields) {
      setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
      setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
      setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
      setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
    } else {
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-income-details-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantIncomeDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantIncomeDetails.form.h1}>
                {t('CoapplicantIncomeDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantIncomeDetails.form.h2}>
                {t('CoapplicantIncomeDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantIncomeDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .incomeTypeSelect
                    }
                    label={t('CoapplicantIncomeDetailsPage.incomeType')}
                    name="incomeTypeValue"
                    options={incomeTypeOptions}
                    rules={incomeTypeValidation}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      error={!isEmpty(errors.incomeEmployerNameValue)}
                      label={t('CoapplicantIncomeDetailsPage.employerName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantIncomeDetails.form
                              .employerNameInput,
                        },
                      }}
                      value={
                        watch(
                          'incomeEmployerNameValue',
                          coapplicantAdditionalIncomeEmployerName || '',
                        ) ?? ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantIncomeDetails.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      error={!isEmpty(errors.incomeJobTitleValue)}
                      label={t('CoapplicantIncomeDetailsPage.jobTitle')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantIncomeDetails.form
                              .jobTitleInput,
                        },
                      }}
                      value={
                        watch(
                          'incomeJobTitleValue',
                          coapplicantAdditionalIncomeJobTitle || '',
                        ) ?? ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantIncomeDetails.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .yearSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantIncomeDetails.form.yearSelect
                        }
                        label={t('CoapplicantIncomeDetailsPage.durationYears')}
                        name="incomeDurationYearsValue"
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationYearsValidation}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantIncomeDetails.form
                            .monthSelect
                        }
                        label={t('CoapplicantIncomeDetailsPage.durationMonths')}
                        name="incomeDurationMonthsValue"
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationMonthsValidation}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantIncomeDetails.form
                        .frequencySelect
                    }
                    label={t('CoapplicantIncomeDetailsPage.incomeFrequency')}
                    name="incomeFrequencyValue"
                    options={incomeFrequencyOptions}
                    rules={incomeFrequencyValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    error={!isEmpty(errors.incomeAmountValue)}
                    label={t('CoapplicantIncomeDetailsPage.incomeAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantIncomeDetails.form
                            .amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.incomeAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeAmountValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantIncomeDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantIncomeDetailsPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantIncomeDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantIncomeDetailsPage';
